import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { QAQC_METRIC_CATEGORIES, QAQC_METRIC_CATEGORY_LABELS } from '@qms360/shared';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import { DerivedFigure, StatusChip, VarianceBadge } from '../../components/qaqc/DerivedFigure.js';
import { fetchDashboard } from '../../lib/api.js';
import { t } from '../../lib/i18n.js';

/**
 * The Main Dashboard (spec §9), filterable by project group, project, month and
 * category.
 *
 * Every figure here comes from the server, which computed it with the same shared
 * helpers the entry screens use. The screen does no arithmetic of its own —
 * ADR-0007's guarantee is that a dashboard and a report of the same period cannot
 * disagree, and the only way to hold that is to have one implementation.
 */
export function Dashboard() {
  const [filters, setFilters] = useState({
    projectGroup: '',
    category: '',
    fromPeriod: '',
    toPeriod: '',
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['qaqc-dashboard', filters],
    queryFn: () =>
      fetchDashboard({
        ...(filters.projectGroup ? { projectGroup: filters.projectGroup } : {}),
        ...(filters.category ? { category: filters.category } : {}),
        ...(filters.fromPeriod ? { fromPeriod: `${filters.fromPeriod}-01` } : {}),
        ...(filters.toPeriod ? { toPeriod: `${filters.toPeriod}-01` } : {}),
      }),
  });

  const anyProvisional = data?.rows.some((row) => row.isProvisional) ?? false;

  return (
    <div className="min-h-full">
      <AppTopBar app="qaqc" />
      <Breadcrumbs app="qaqc" trail={['Dashboard']} />

      <main className="mx-auto max-w-7xl px-4 pb-12 pt-4">
        <h1 className="m-0 text-2xl font-semibold">{t("QA/QC Dashboard")}</h1>

        {/* --- Filters --------------------------------------------------- */}
        <section className="mt-4 rounded-md border border-border bg-surface p-5 shadow-card">
          <h2 className="sr-only">{t("Filters")}</h2>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label htmlFor="filter-group" className="text-xs font-medium text-content-secondary">
                {t("Project group")}
              </label>
              <input
                id="filter-group"
                value={filters.projectGroup}
                onChange={(event) =>
                  setFilters({ ...filters, projectGroup: event.target.value })
                }
                placeholder={t("All groups")}
                className="mt-1 w-48 rounded-sm border border-border-strong px-2 py-1.5 text-sm"
              />
            </div>

            <div>
              <label
                htmlFor="filter-category"
                className="text-xs font-medium text-content-secondary"
              >
                {t("Category")}
              </label>
              <select
                id="filter-category"
                value={filters.category}
                onChange={(event) => setFilters({ ...filters, category: event.target.value })}
                className="mt-1 rounded-sm border border-border-strong px-2 py-1.5 text-sm"
              >
                <option value="">{t("All categories")}</option>
                {QAQC_METRIC_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {t(QAQC_METRIC_CATEGORY_LABELS[category])}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="filter-from" className="text-xs font-medium text-content-secondary">
                {t("From")}
              </label>
              <input
                id="filter-from"
                type="month"
                value={filters.fromPeriod}
                onChange={(event) => setFilters({ ...filters, fromPeriod: event.target.value })}
                className="mt-1 rounded-sm border border-border-strong px-2 py-1.5 text-sm"
              />
            </div>

            <div>
              <label htmlFor="filter-to" className="text-xs font-medium text-content-secondary">
                {t("To")}
              </label>
              <input
                id="filter-to"
                type="month"
                value={filters.toPeriod}
                onChange={(event) => setFilters({ ...filters, toPeriod: event.target.value })}
                className="mt-1 rounded-sm border border-border-strong px-2 py-1.5 text-sm"
              />
            </div>

            <button
              type="button"
              onClick={() =>
                setFilters({ projectGroup: '', category: '', fromPeriod: '', toPeriod: '' })
              }
              className="rounded-sm border border-border-strong px-3 py-1.5 text-sm"
            >
              {t("Clear")}
            </button>
          </div>

          {data && filters.category && (
            <p className="mt-3 text-xs text-content-secondary">
              {t("Counts and closure rates below are for")}{' '}
              {t(QAQC_METRIC_CATEGORY_LABELS[filters.category as keyof typeof QAQC_METRIC_CATEGORY_LABELS])}{' '}
              {t("only. The Quality Index always spans every category, so it is unaffected by this filter.")}
            </p>
          )}
        </section>

        {isLoading && <p className="mt-6 text-content-secondary">{t("Loading…")}</p>}
        {error && <p className="mt-6 text-tone-critical">{t("Could not load the dashboard.")}</p>}

        {data && (
          <>
            {/* --- Summary ---------------------------------------------- */}
            <section className="mt-6 rounded-md border border-border bg-surface p-5 shadow-card">
              <dl className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-6">
                <DerivedFigure label={t("Projects")} value={data.summary.projectCount} />
                <DerivedFigure label={t("Periods")} value={data.summary.periodCount} />
                <DerivedFigure label={t("Approved")} value={data.summary.approvedCount} />
                <DerivedFigure
                  label={t("Closure rate")}
                  value={data.summary.closureRate}
                  suffix="%"
                  explanation={data.summary.closureRateExplanation}
                />
                <DerivedFigure
                  label={t("Average index")}
                  value={data.summary.averagePqi}
                  explanation={
                    data.summary.averagePqi === null
                      ? 'No period in this selection has a quality index yet.'
                      : null
                  }
                />
                <DerivedFigure label={t("Open over 45 days")} value={data.summary.ageingOver45} />
              </dl>

              {data.target !== null && data.summary.belowTargetPeriods > 0 && (
                <p className="mt-4 rounded-sm bg-tone-pending-surface px-3 py-2 text-sm">
                  {data.summary.belowTargetPeriods}{' '}{t("of")}{' '}{data.summary.periodCount}{' '}{t("periods are below the")}{' '}{data.target}{t("% closure-rate target.")}
                </p>
              )}

              {anyProvisional && (
                <p role="note" className="mt-3 text-xs text-content-secondary">
                  <strong className="font-semibold">{t("Provisional weighting.")}</strong>{' '}{t("The Quality Index formula is pending Algihaz confirmation.")}
                </p>
              )}
            </section>

            {/* --- Rows -------------------------------------------------- */}
            {data.rows.length === 0 ? (
              <p className="mt-6 rounded-md border border-border bg-tone-info-surface p-4 text-sm text-content-secondary">
                {t("No reporting periods match these filters.")}
              </p>
            ) : (
              <div className="mt-6 overflow-x-auto rounded-md border border-border bg-surface shadow-card">
                <table className="w-full border-collapse text-sm">
                  <caption className="sr-only">
                    {t("QA/QC reporting periods matching the selected filters")}
                  </caption>
                  <thead>
                    <tr className="border-b border-border text-start">
                      <th scope="col" className="p-3 text-start font-semibold">{t("Project")}</th>
                      <th scope="col" className="p-3 text-start font-semibold">{t("Period")}</th>
                      <th scope="col" className="p-3 text-start font-semibold">{t("Status")}</th>
                      <th scope="col" className="p-3 text-end font-semibold">{t("Issued")}</th>
                      <th scope="col" className="p-3 text-end font-semibold">{t("Closed")}</th>
                      <th scope="col" className="p-3 text-end font-semibold">{t("Closure rate")}</th>
                      <th scope="col" className="p-3 text-end font-semibold">{t("Over 45d")}</th>
                      <th scope="col" className="p-3 text-end font-semibold">{t("Index")}</th>
                      <th scope="col" className="p-3 text-end font-semibold">{t("MoM")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row) => (
                      <tr key={row.periodId} className="border-b border-border last:border-0">
                        <td className="p-3">
                          <Link
                            to={`/qaqc/periods/${row.periodId}`}
                            className="text-ag-purple no-underline hover:underline"
                          >
                            {row.projectCode}
                          </Link>
                          {row.projectGroup && (
                            <span className="ms-2 text-xs text-content-secondary">
                              {row.projectGroup}
                            </span>
                          )}
                        </td>
                        <td className="p-3">{row.periodStart.slice(0, 7)}</td>
                        <td className="p-3">
                          <StatusChip status={row.status} />
                        </td>
                        <td className="p-3 text-end">{row.issued}</td>
                        <td className="p-3 text-end">{row.closed}</td>
                        <td
                          className="p-3 text-end"
                          title={row.closureRateExplanation}
                        >
                          {/* Null renders as an em dash, never 0% (ADR-0007). */}
                          {row.closureRate === null ? (
                            <span className="text-content-secondary">—</span>
                          ) : (
                            <span
                              className={
                                row.belowTarget ? 'font-semibold text-tone-critical' : undefined
                              }
                            >
                              {row.closureRate}%
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-end">
                          {row.ageingOver45 > 0 ? (
                            <span className="font-semibold text-tone-pending">
                              {row.ageingOver45}
                            </span>
                          ) : (
                            row.ageingOver45
                          )}
                        </td>
                        <td className="p-3 text-end">
                          {row.pqiValue === null ? (
                            <span className="text-content-secondary">—</span>
                          ) : (
                            row.pqiValue
                          )}
                        </td>
                        <td className="p-3 text-end">
                          <VarianceBadge
                            value={row.pqiVariance === null ? null : String(row.pqiVariance)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
